import Spacing from '@app/components/common/Spacing';
import PageTitle from '@app/shared/components/PageTitle';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Divider,
  MenuItem,
  TextField,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
} from '@material-ui/core';
import { groupBy as rowGrouper, sumBy } from 'lodash';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useGetLedgerMatrixReportByOrganizationIdQuery,
  GetLedgerMatrixReportByOrganizationIdQuery,
  useGetOrganizationsQuery,
} from '@app/generated/graphql';
import { listPageContent } from '@app/shared/hoc/list-page';
import { FormLayout, FormLayoutGroup, InfoCard } from '@mystiny/ui';
import { FormProvider, useForm } from 'react-hook-form';
import { DateUtils } from '@app/shared/libs/date';
import { Select } from '@aginix/mui-react-hook-form-input';
import AutocompleteInput from '@app/shared/components/AutocompleteInput';
import { useIfUser } from '@app/components/common/Routes';
import { ExportButton } from './export/ExportButton';
import NumberFormat from 'react-number-format';

import { TabPanel } from '@app/components/TabPanel';
import { useCurrentUser } from '@app/hooks';

type RowData = {
  name: string;
  budgets: {
    [key: string]: {
      budget: number;
    };
  };
  total: number;
  [key: string]: any;
};

const MatrixReportDataTable = ({
  budget_rows,
  account_rows,
  account_budget_balance_rows,
}: {
  budget_rows: GetLedgerMatrixReportByOrganizationIdQuery['budget_rows'];
  account_rows: GetLedgerMatrixReportByOrganizationIdQuery['account_rows'];
  account_budget_balance_rows: GetLedgerMatrixReportByOrganizationIdQuery['expense_account_budget_balance_rows'];
}) => {
  const groupRows = rowGrouper(account_budget_balance_rows, 'budget_group_name');
  const groupNames = Object.keys(groupRows);

  const groupBudgets = rowGrouper(budget_rows, 'group_name');
  const budgetAllocationRow = useMemo<RowData>(() => {
    const budgets = groupNames.reduce(
      (prev, name) => ({
        ...prev,
        [name]: { budget: sumBy(groupBudgets[name], ({ allocations }) => sumBy(allocations, 'amount')) },
      }),
      {}
    );
    const total = sumBy(Object.values(budgets), 'budget');

    return {
      name: 'วงเงินจัดสรร',
      budgets,
      total,
    };
  }, [groupBudgets, groupNames]);

  const rows = useMemo<RowData[]>(
    () =>
      account_rows.map((account) => {
        const budgets = groupNames.reduce(
          (prev, groupName) => ({
            ...prev,
            [groupName]: {
              budget: sumBy(
                groupRows[groupName].filter((row) => row.account_id === account.id),
                'balance'
              ),
            },
          }),
          {}
        );

        const total = sumBy(
          account_budget_balance_rows.filter((row) => row.account_id === account.id),
          'balance'
        );

        return {
          name: account.code + ' ' + account.name,
          account: account,
          budgets: budgets,
          total: total,
        };
      }),
    [account_rows, groupNames, account_budget_balance_rows, groupRows]
  );

  const summaryRow: RowData = useMemo(() => {
    const budgets = groupNames.reduce(
      (prev, groupName) => ({
        ...prev,
        [groupName]: {
          budget: sumBy(groupRows[groupName], 'balance'),
        },
      }),
      {}
    );

    const total = sumBy(account_budget_balance_rows, 'balance');

    return { name: 'รวมทั้งหมด', budgets, total: total };
  }, [groupNames, account_budget_balance_rows, groupRows]);

  const outstandingRow = useMemo<RowData>(() => {
    const budgets = groupNames.reduce(
      (prev, groupName) => ({
        ...prev,
        [groupName]: {
          budget: budgetAllocationRow.budgets[groupName].budget - Math.abs(summaryRow.budgets[groupName].budget),
        },
      }),
      {}
    );

    const total = sumBy(Object.values(budgets), 'budget');

    return {
      name: 'คงเหลือ',
      budgets,
      total: total,
    };
  }, [budgetAllocationRow.budgets, groupNames, summaryRow.budgets]);

  const exportData = [budgetAllocationRow, ...rows, summaryRow, outstandingRow];

  return (
    <Fragment>
      <Grid container justify="flex-end" spacing={1}>
        <Grid item>
          <ExportButton data={exportData} name="รายงาน Matrix Report2" />
        </Grid>
      </Grid>
      <Spacing />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: 230 }} align="center">
                รหัสและชื่อบัญชี
              </TableCell>
              {groupNames.map((groupName) => (
                <TableCell key={`TableHead-${groupName}`} align="center" style={{ minWidth: 160 }}>
                  {groupName}
                </TableCell>
              ))}
              <TableCell align="center" style={{ minWidth: 120, maxWidth: 160 }}>
                รวม
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center" style={{ minWidth: 230 }}>
                วงเงินได้รับจัดสรร
              </TableCell>
              {groupNames.map((groupName) => (
                <TableCell key={`TableHead-${groupName}-Allocation`} align="center" style={{ minWidth: 160 }}>
                  <NumberFormat
                    value={budgetAllocationRow.budgets[groupName]?.budget || '0'}
                    displayType={'text'}
                    type="text"
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                  />
                </TableCell>
              ))}
              <TableCell align="center" style={{ minWidth: 120, maxWidth: 160 }}>
                <NumberFormat
                  value={budgetAllocationRow.total}
                  displayType={'text'}
                  type="text"
                  decimalScale={2}
                  fixedDecimalScale
                  thousandSeparator
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ account, budgets, total }) => (
              <TableRow key={account.id}>
                <TableCell style={{ minWidth: 230 }}>
                  {account.code} {account.name}
                </TableCell>
                {groupNames.map((groupName) => (
                  <TableCell key={`TableCell-${groupName}`} align="right">
                    <NumberFormat
                      value={budgets[groupName]?.budget || 0}
                      displayType={'text'}
                      type="text"
                      decimalScale={2}
                      fixedDecimalScale
                      thousandSeparator
                    />
                  </TableCell>
                ))}
                <TableCell align="right">
                  <NumberFormat
                    value={total}
                    displayType={'text'}
                    type="text"
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell align="right">รวมทั้งหมด</TableCell>
              {groupNames.map((groupName) => (
                <TableCell key={`TableCell-SummaryRow-${groupName}`} align="right">
                  <NumberFormat
                    value={summaryRow.budgets[groupName]?.budget || 0}
                    displayType={'text'}
                    type="text"
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                  />
                </TableCell>
              ))}

              <TableCell align="right" style={{ minWidth: 120, maxWidth: 160 }}>
                <NumberFormat
                  value={summaryRow.total}
                  displayType={'text'}
                  type="text"
                  decimalScale={2}
                  fixedDecimalScale
                  thousandSeparator
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">คงเหลือ</TableCell>
              {groupNames.map((groupName) => (
                <TableCell key={`TableCell-OutstandingRow-${groupName}`} align="right">
                  <NumberFormat
                    value={outstandingRow.budgets[groupName]?.budget || 0}
                    displayType={'text'}
                    type="text"
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                  />
                </TableCell>
              ))}
              <TableCell align="right" style={{ minWidth: 120, maxWidth: 160 }}>
                <NumberFormat
                  value={outstandingRow.total}
                  displayType={'text'}
                  type="text"
                  decimalScale={2}
                  fixedDecimalScale
                  thousandSeparator
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Fragment>
  );
};

const MatrixReportView2 = () => {
  const user = useCurrentUser();
  const isSysadmin = useIfUser({ condition: ({ roles }) => roles.includes('sysadmin') });
  const form = useForm({
    defaultValues: {
      fiscal_year: DateUtils.getThaiFiscalYear(new Date()),
      organization_id: user?.organizationId,
    },
  });
  const [value, setValue] = useState(0);
  const handleChange = useCallback((_, value: number) => {
    setValue(value);
  }, []);

  useEffect(() => {
    form.setValue('organization_id', user?.organizationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fiscalYear = form.watch('fiscal_year', DateUtils.getThaiFiscalYear(new Date()));
  const organizationId = form.watch('organization_id');

  const { data: organizationData, loading: organizationLoading } = useGetOrganizationsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const { data, loading } = useGetLedgerMatrixReportByOrganizationIdQuery({
    variables: {
      fiscalYear: Number(fiscalYear),
      organizationId: form.watch('organization_id'),
    },
  });

  const organization = useMemo(
    () => organizationData?.rows.find((org) => org.id === organizationId),
    [organizationData, organizationId]
  );

  const organizationOptions = useMemo(
    () => [{ id: undefined, name: 'ภาพรวม' }, ...(organizationData?.rows || [])],
    [organizationData]
  );

  return (
    <Fragment>
      <PageTitle
        title={
          <Typography variant="h5" color="primary">
            Matrix Report 2
          </Typography>
        }
        actions={<Fragment></Fragment>}
      />
      <Spacing />
      <InfoCard>
        <Grid container spacing={2} justify="space-between">
          <Grid item md={5}>
            <Typography variant="h6" color="primary">
              {loading ? '....' : organization?.name || 'ระดับภาพรวมทุกหน่วยงาน'}
            </Typography>
            <Typography variant="h6" color="textSecondary">
              ปีงบประมาณพ.ศ. {fiscalYear}
            </Typography>
          </Grid>
          <Grid item md={7}>
            <FormProvider {...form}>
              <form noValidate onSubmit={form.handleSubmit((formData) => console.log(formData))}>
                <FormLayout>
                  <FormLayoutGroup>
                    <FormControl fullWidth margin="dense">
                      {isSysadmin ? (
                        <AutocompleteInput
                          label="หน่วยงาน"
                          name="organization_id"
                          options={organizationOptions}
                          loading={organizationLoading}
                          disableClearable
                          primaryKey="id"
                          getOptionLabel={(option: any) => `${option.name}`}
                          size="small"
                        />
                      ) : (
                        <TextField
                          label="หน่วยงาน"
                          size="small"
                          name="organization_name"
                          id="organization_name"
                          variant="outlined"
                          value={user?.organization?.name}
                          disabled
                        />
                      )}
                    </FormControl>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel htmlFor="fiscal_year">ปีงบประมาณ</InputLabel>
                      <Select name="fiscal_year" id="fiscal_year" label="ปีงบประมาณ">
                        <MenuItem value="2564">2564</MenuItem>
                        <MenuItem value="2563">2563</MenuItem>
                      </Select>
                    </FormControl>
                  </FormLayoutGroup>
                </FormLayout>
              </form>
            </FormProvider>
          </Grid>
        </Grid>
        <Spacing />
        <Divider />
        <Spacing />
        {loading ? (
          <CircularProgress />
        ) : (
          <Fragment>
            {data ? (
              <Fragment>
                <Tabs
                  value={value}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={handleChange}
                  aria-label="disabled tabs example"
                >
                  <Tab label="EX1" />
                  <Tab label="EX2" />
                </Tabs>

                <TabPanel value={value} index={0}>
                  <MatrixReportDataTable
                    budget_rows={data.budget_rows}
                    account_rows={data.account_rows}
                    account_budget_balance_rows={data.expense_account_budget_balance_rows}
                  />
                </TabPanel>
                <TabPanel value={value} index={1}>
                  <MatrixReportDataTable
                    budget_rows={data.budget_rows}
                    account_rows={data.account_rows}
                    account_budget_balance_rows={data.debt_account_budget_balance_rows as any}
                  />
                </TabPanel>
              </Fragment>
            ) : (
              <Box mt={4}>
                <Typography variant="h5" color="primary">
                  ไม่พบข้อมูล
                </Typography>
              </Box>
            )}
          </Fragment>
        )}
      </InfoCard>
    </Fragment>
  );
};

export default listPageContent(MatrixReportView2);
